import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { defer, forkJoin, from, iif, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { RsTemplateSubmittingService } from '../../../services/rs-template-submitting.service';
import { GenericDialogService } from '../../../services/ui/generic-dialog.service';
import { ConceptModel } from '../../../types/concept-node';
import {
  NoticeActionDto,
  NoticeControllerService,
  ProcedureControllerService,
  ProcedureOverviewDto,
} from '../../api/notices';
import { ErrorType } from '../../shared/error-type.enum';
import { NavigationUtil } from '../../shared/navigation.util';
import { RefreshNoticeLockService } from '../../shared/refresh-notice-lock.service';
import { StateService } from '../../shared/state.service';
import { NoticeOverviewViewModel } from '../NoticeOverviewViewModel';
import { BekanntmachungenVerwaltungState, initialState } from './bekanntmachungen-verwaltung-state.model';
import { BekanntmachungenVerwaltungService } from './bekanntmachungen-verwaltung.service';
import { UserSettingsStateService } from '../../shared/user-settings-state.service';

@Injectable({
  providedIn: 'root',
})
export class BekanntmachungenVerwaltungStateService extends StateService<BekanntmachungenVerwaltungState> {
  loading$: Observable<boolean> = this.select(state => state.loading);
  procedures$: Observable<ProcedureOverviewDto[]> = this.select(state => state.procedures);
  filter$: Observable<{ showFavorites: boolean; showHidden: boolean; }> = this.select(state => ({
    showFavorites: state.showFavorites,
    showHidden: state.showHidden,
  }));

  private keycloak = inject(KeycloakService);
  private procedureControllerService = inject(ProcedureControllerService);
  private userSettingsStateService = inject(UserSettingsStateService);
  private bekanntmachungenVerwaltungService = inject(BekanntmachungenVerwaltungService);
  private noticeControllerService = inject(NoticeControllerService);
  private refreshNoticeLockService = inject(RefreshNoticeLockService);
  private noticeService = inject(NoticeControllerService);
  private rsTemplateService = inject(RsTemplateSubmittingService);
  private router = inject(Router);
  private genericDialogService = inject(GenericDialogService);

  constructor() {
    super(initialState);
  }

  init(): void {
    this.setState(initialState);

    this.doWithLoading(
      from(this.keycloak.loadUserProfile()).pipe(
        tap(loggedInUser => this.setState({ loggedInUser, selectedUserId: loggedInUser.id })),
        switchMap(loggedInUser => forkJoin({
          procedures: this.procedureControllerService.getProceduresOverview(loggedInUser.id),
        })),
        tap(({ procedures }) => this.setState({ procedures })),
      ),
    ).subscribe();
  }

  selectUser(selectedUserId: string): void {
    this.setState({ selectedUserId });
    this.doWithLoading(
      this.procedureControllerService.getProceduresOverview(selectedUserId),
    ).subscribe(procedures => this.setState({ procedures }));
  }

  updateFavoriteFilter(checked: boolean): void {
    this.setState({ showFavorites: checked });
  }

  updateHiddenFilter(checked: boolean): void {
    this.setState({ showHidden: checked });
  }

  editDraft(action: NoticeActionDto, notice: NoticeOverviewViewModel, activatedRoute: ActivatedRoute): void {
    const { subTypeId, latestSdkVersion } = notice.notice;

    const navigate$ = iif(
      () => latestSdkVersion,
      of(true),
      this.confirmEditDraftModalDialog(),
    );

    navigate$
      .pipe(
        filter(Boolean),
        switchMap(() => this.navigate(action, notice, subTypeId, activatedRoute)),
      )
      .subscribe();
  }

  deleteDraft(notice: NoticeOverviewViewModel): void {
    const { rsNoticeId } = notice.notice;

    from(this.confirmDeleteDraftModalDialog())
      .pipe(
        filter(Boolean),
        switchMap(() => {
          const procedureOfDraft = this.state.procedures.find(procedure => procedure.notices.find(notice => notice.rsNoticeId === rsNoticeId));
          const cleanFavoritesAndHiddenCall$ = procedureOfDraft?.notices?.length === 1 ? this.userSettingsStateService.removeProcedureFromFavoriteAndHiddenLists(procedureOfDraft.id) : of(null);

          return this.doWithLoading(
            cleanFavoritesAndHiddenCall$.pipe(
              switchMap(() => this.noticeControllerService.deleteNotice(rsNoticeId)),
              switchMap(() => this.procedureControllerService.getProceduresOverview(this.state.selectedUserId)),
              tap((procedures: ProcedureOverviewDto[]) => this.setState({ procedures })),
              catchError(err => {
                if (err.error.errorType === ErrorType.NOTICE_LOCKED) {
                  return this.refreshNoticeLockService.showNoticeLockedErrorDialog('Löschen aktuell nicht möglich');
                }
                return throwError(() => err);
              }),
            ),
          );
        }),
      )
      .subscribe();
  }

  showPreview(notice: NoticeOverviewViewModel): void {
    const { rsNoticeId } = notice.notice;
    this.bekanntmachungenVerwaltungService.getPreview(rsNoticeId).subscribe();
  }

  exportNotice(notice: NoticeOverviewViewModel): void {
    const { rsNoticeId } = notice.notice;
    this.bekanntmachungenVerwaltungService.exportNotice(rsNoticeId).subscribe();
  }

  stop(notice: NoticeOverviewViewModel): void {
    const { rsNoticeId } = notice.notice;

    from(this.confirmStopNoticeModalDialog())
      .pipe(
        filter(Boolean),
        switchMap(() => {
          return this.doWithLoading(
            this.noticeControllerService.stopNotice(rsNoticeId).pipe(
              switchMap(() => this.procedureControllerService.getProceduresOverview(this.state.selectedUserId)),
              tap((procedures: ProcedureOverviewDto[]) => this.setState({ procedures })),
            ),
          );
        }),
      )
      .subscribe();
  }

  createTemplate(notice: NoticeOverviewViewModel): void {
    const { rsNoticeId, latestSdkVersion } = notice.notice;

    this.doWithLoading(
      this.noticeService
        .getNoticeFormValueForSavedNotice(rsNoticeId)
        .pipe(
          map(json => json as unknown as ConceptModel),
          switchMap(conceptModel =>
            this.rsTemplateService.createTemplateFromNotice(conceptModel).pipe(
              switchMap(rsTemplateId =>
                this.router.navigate(
                  ['vorlagen', 'anlegen', 'notice', conceptModel.noticeSubTypeCode, 'details'],
                  {
                    queryParams: NavigationUtil.buildQueryParamsTemplate(!latestSdkVersion, rsTemplateId),
                  },
                ),
              ),
            ),
          ),
        ),
    ).subscribe();
  }

  createUpdateOrSuccessor(action: NoticeActionDto, notice: NoticeOverviewViewModel, subTypeId: string, activatedRoute: ActivatedRoute): void {
    const { latestSdkVersion } = notice.notice;

    const navigate$ = iif(
      () => latestSdkVersion,
      of(true),
      this.confirmCreateUpdateOrSuccessorModalDialog(),
    );

    navigate$
      .pipe(
        filter(Boolean),
        switchMap(() => this.navigate(action, notice, subTypeId, activatedRoute)),
      )
      .subscribe();
  }

  private confirmStopNoticeModalDialog(): Observable<boolean> {
    return defer(() =>
      this.genericDialogService.confirmWithDialog(
        'Bekanntmachung zurückziehen',
        'Bekanntmachung zurückziehen',
        'Abbrechen',
        'Sind Sie sicher, dass Sie die Bekanntmachung zurückziehen wollen?',
      ),
    );
  }

  private confirmDeleteDraftModalDialog(): Observable<boolean> {
    return defer(() =>
      this.genericDialogService.confirmWithDialog(
        'Entwurf löschen',
        'Entwurf löschen',
        'Abbrechen',
        'Sind Sie sicher, dass Sie den Entwurf löschen möchten?',
      ),
    );
  }

  private confirmEditDraftModalDialog(): Observable<boolean> {
    return defer(() =>
      this.genericDialogService.confirmWithWarningDialog(
        'WARNUNG: Aktualisierung des eForms-Standards',
        'Akzeptieren',
        'Abbrechen',
        '<b>Grund der Warnung</b><br>' +
        'Der eForms-Standard wurde aktualisiert, seit diese Bekanntmachung das letzte Mal bearbeitet wurde.<br><br>' +
        '<b>Was ist zu beachten?</b><br>' +
        'Nach dem Öffnen können Sie die Bekanntmachung wie gewohnt anzeigen und bearbeiten. Bitte überprüfen ' +
        'Sie vor dem Versenden der Bekanntmachung, ob alle wichtigen Daten vorhanden sind. Unter Umständen ' +
        'kann es bei der Aktualisierung des eForms-Standards zu einem teilweisen Datenverlust kommen.',
      ),
    );
  }

  private confirmCreateUpdateOrSuccessorModalDialog(): Observable<boolean> {
    return defer(() =>
      this.genericDialogService.confirmWithWarningDialog(
        'WARNUNG: Aktualisierung des eForms-Standards',
        'Akzeptieren',
        'Abbrechen',
        '<b>Grund der Warnung</b><br>' +
        'Der eForms-Standard wurde aktualisiert, seit die ursprüngliche Bekanntmachung versendet wurde.<br><br>' +
        '<b>Was ist zu beachten?</b><br>' +
        'Nach dem Öffnen können Sie die Bekanntmachung wie gewohnt bearbeiten. Bitte überprüfen ' +
        'Sie vor dem Versenden der Bekanntmachung, ob alle wichtigen Daten vorhanden sind. Unter Umständen ' +
        'kann es bei der automatischen Vorbefüllung der Felder mit den Werten aus der ursprünglichen Bekanntmachung zu Fehlern kommen.',
      ),
    );
  }

  private navigate(action: NoticeActionDto, notice: NoticeOverviewViewModel, subTypeId: string, activatedRoute: ActivatedRoute) {
    return from(
      this.router.navigate(['notice', subTypeId, 'details'], {
        relativeTo: activatedRoute,
        queryParams: NavigationUtil.buildQueryParamsNotice(action, notice),
      }),
    );
  }

  private doWithLoading<T>(action$: Observable<T>): Observable<T> {
    this.setState({ loading: true });
    return action$.pipe(
      finalize(() => this.setState({ loading: false })),
    );
  }
}
